import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Phone, ArrowRight, Shield, MessageCircle, Users, Lock, Fingerprint, KeyRound, Download, Smartphone } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_social-circle-37/artifacts/vzzvahrb_IMG-20251227-WA0000.jpg";

// Country codes list
const COUNTRY_CODES = [
  { code: "+91", country: "India", flag: "🇮🇳", maxLength: 10 },
  { code: "+1", country: "USA/Canada", flag: "🇺🇸", maxLength: 10 },
  { code: "+44", country: "UK", flag: "🇬🇧", maxLength: 10 },
  { code: "+971", country: "UAE", flag: "🇦🇪", maxLength: 9 },
  { code: "+966", country: "Saudi Arabia", flag: "🇸🇦", maxLength: 9 },
  { code: "+974", country: "Qatar", flag: "🇶🇦", maxLength: 8 },
  { code: "+968", country: "Oman", flag: "🇴🇲", maxLength: 8 },
  { code: "+973", country: "Bahrain", flag: "🇧🇭", maxLength: 8 },
  { code: "+965", country: "Kuwait", flag: "🇰🇼", maxLength: 8 },
  { code: "+61", country: "Australia", flag: "🇦🇺", maxLength: 9 },
  { code: "+65", country: "Singapore", flag: "🇸🇬", maxLength: 8 },
  { code: "+60", country: "Malaysia", flag: "🇲🇾", maxLength: 10 },
  { code: "+49", country: "Germany", flag: "🇩🇪", maxLength: 11 },
  { code: "+33", country: "France", flag: "🇫🇷", maxLength: 9 },
  { code: "+39", country: "Italy", flag: "🇮🇹", maxLength: 10 },
  { code: "+81", country: "Japan", flag: "🇯🇵", maxLength: 10 },
  { code: "+86", country: "China", flag: "🇨🇳", maxLength: 11 },
  { code: "+82", country: "South Korea", flag: "🇰🇷", maxLength: 10 },
  { code: "+27", country: "South Africa", flag: "🇿🇦", maxLength: 9 },
  { code: "+254", country: "Kenya", flag: "🇰🇪", maxLength: 9 },
];

const AuthPage = () => {
  // States: phone | auth-method | otp | pin | biometric | forgot-pin
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [otp, setOtp] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [hasBiometric, setHasBiometric] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // PWA Install setup
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check for iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // For Android/Desktop - listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      return;
    }

    if (!deferredPrompt) {
      toast.info("App can be installed from your browser menu");
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success("App installed successfully!");
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
  };

  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0];

  useEffect(() => {
    // Check if Web Authentication API is available
    if (window.PublicKeyCredential) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then((available) => {
          setBiometricSupported(available);
        })
        .catch(() => {
          setBiometricSupported(false);
        });
    }
  }, []);

  const checkAuthMethods = async (phoneNumber) => {
    try {
      const response = await api.post("/auth/pin/check", { phone: phoneNumber, country_code: countryCode });
      return response.data;
    } catch (_error) {
      return { has_pin: false, has_biometric: false, user_exists: false };
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < selectedCountry.maxLength) {
      toast.error(`Please enter a valid ${selectedCountry.maxLength}-digit phone number`);
      return;
    }

    setLoading(true);
    try {
      // Check if user has PIN/Biometric set up
      const authMethods = await checkAuthMethods(phone);
      
      if (authMethods.user_exists && (authMethods.has_pin || authMethods.has_biometric)) {
        setHasPin(authMethods.has_pin);
        setHasBiometric(authMethods.has_biometric);
        setStep("auth-method");
      } else {
        // New user or user without PIN - proceed with OTP
        await sendOTP();
      }
    } catch (_error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sendOTP = async () => {
    setLoading(true);
    try {
      const response = await api.post("/auth/send-otp", { phone, country_code: countryCode });
      if (response.data.success) {
        toast.success(response.data.message);
        setStep("otp");
      } else {
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.detail || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e?.preventDefault();
    await sendOTP();
  };

  const handleVerifyOTP = async (value) => {
    if (value.length !== 6) return;
    
    setLoading(true);
    try {
      const response = await api.post("/auth/verify-otp", { phone, otp: value, country_code: countryCode });
      login(response.data.user, response.data.token, response.data.is_new_user);
      toast.success("Welcome to LUNAWAT FOUNDATION!");
      
      // For new users OR users without PIN: go to PIN setup first
      if (!response.data.user.has_pin) {
        navigate("/pin-setup");
      } else if (!response.data.user.is_profile_complete) {
        navigate("/onboarding");
      } else if (response.data.user.verification_status !== "verified") {
        navigate("/pending-verification");
      } else {
        navigate("/welcome");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handlePinLogin = async (value) => {
    if (value.length !== 6) return;
    
    setLoading(true);
    try {
      const response = await api.post("/auth/pin/login", { phone, pin: value, country_code: countryCode });
      login(response.data.user, response.data.token, false);
      toast.success("Welcome back!");
      
      // Route based on profile status
      if (!response.data.user.is_profile_complete) {
        navigate("/onboarding");
      } else if (response.data.user.verification_status !== "verified") {
        navigate("/pending-verification");
      } else {
        navigate("/welcome");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid PIN");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    setLoading(true);
    try {
      // Create assertion for WebAuthn
      const publicKeyCredentialRequestOptions = {
        challenge: new Uint8Array(32),
        allowCredentials: [],
        userVerification: "required",
        timeout: 60000,
      };

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
      });

      if (assertion) {
        // Verify with server
        const response = await api.post("/auth/biometric/login", { phone, country_code: countryCode });
        login(response.data.user, response.data.token, false);
        toast.success("Welcome back!");
        
        // Route based on profile status
        if (!response.data.user.is_profile_complete) {
          navigate("/onboarding");
        } else if (response.data.user.verification_status !== "verified") {
          navigate("/pending-verification");
        } else {
          navigate("/welcome");
        }
      }
    } catch (error) {
      console.error("Biometric login error:", error);
      toast.error("Biometric authentication failed. Please use PIN or OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPin = async () => {
    setLoading(true);
    try {
      // Send new OTP for PIN reset
      await api.post("/auth/send-otp", { phone, country_code: countryCode });
      toast.success("OTP sent for PIN reset. Please verify to set new PIN.");
      setStep("forgot-pin");
      setOtp("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPinOTP = async (value) => {
    if (value.length !== 6) return;
    
    setLoading(true);
    try {
      const response = await api.post("/auth/pin/reset", { phone, otp: value, country_code: countryCode });
      login(response.data.user, response.data.token, false);
      toast.success("OTP verified! Please set your new PIN to continue.");
      navigate("/pin-setup");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const renderPhoneStep = () => (
    <form onSubmit={handlePhoneSubmit} className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Phone Number</label>
        <div className="flex gap-2">
          {/* Country Code Selector */}
          <Select value={countryCode} onValueChange={setCountryCode}>
            <SelectTrigger className="w-[110px] h-12">
              <SelectValue>
                <span className="flex items-center gap-1">
                  <span>{selectedCountry.flag}</span>
                  <span>{selectedCountry.code}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {COUNTRY_CODES.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  <span className="flex items-center gap-2">
                    <span>{country.flag}</span>
                    <span>{country.code}</span>
                    <span className="text-muted-foreground text-xs">{country.country}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Phone Input */}
          <div className="relative flex-1">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="tel"
              placeholder={`Enter ${selectedCountry.maxLength}-digit number`}
              value={phone}
              onChange={(e) => {
                // Only allow digits and limit based on country
                const value = e.target.value.replace(/\D/g, '').slice(0, selectedCountry.maxLength);
                setPhone(value);
              }}
              className="pl-10 h-12 text-base input-focus"
              data-testid="phone-input"
              maxLength={selectedCountry.maxLength}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Enter your {selectedCountry.maxLength}-digit {selectedCountry.country} mobile number
        </p>
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 text-base font-semibold btn-hover"
        disabled={loading || phone.length !== selectedCountry.maxLength}
        data-testid="continue-btn"
      >
        {loading ? (
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary-foreground" />
        ) : (
          <>
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        By continuing, you agree to our Terms of Service and Privacy Policy
      </p>
    </form>
  );

  const renderAuthMethodStep = () => (
    <div className="space-y-4">
      <p className="text-sm text-center text-muted-foreground mb-6">
        Welcome back! Choose how you want to login:
      </p>

      {hasBiometric && biometricSupported && (
        <Button 
          className="w-full h-14 text-base"
          onClick={handleBiometricLogin}
          disabled={loading}
          data-testid="biometric-login-btn"
        >
          <Fingerprint className="w-5 h-5 mr-3" />
          Login with Biometric
        </Button>
      )}

      {hasPin && (
        <Button 
          className="w-full h-14 text-base"
          variant={hasBiometric ? "outline" : "default"}
          onClick={() => setStep("pin")}
          disabled={loading}
          data-testid="pin-login-btn"
        >
          <Lock className="w-5 h-5 mr-3" />
          Login with PIN
        </Button>
      )}

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Or</span>
        </div>
      </div>

      <Button 
        variant="ghost" 
        className="w-full"
        onClick={handleSendOTP}
        disabled={loading}
        data-testid="otp-login-btn"
      >
        <KeyRound className="w-4 h-4 mr-2" />
        Login with OTP instead
      </Button>

      <Button 
        variant="link" 
        className="w-full text-muted-foreground"
        onClick={() => {
          setStep("phone");
          setPhone("");
        }}
        disabled={loading}
      >
        Use a different number
      </Button>
    </div>
  );

  const renderOTPStep = () => (
    <div className="space-y-6">
      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={otp}
          onChange={(value) => {
            setOtp(value);
            if (value.length === 6) {
              handleVerifyOTP(value);
            }
          }}
          disabled={loading}
          data-testid="otp-input"
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={1} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={2} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={3} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={4} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={5} className="w-12 h-14 text-xl" />
          </InputOTPGroup>
        </InputOTP>
      </div>

      {loading && (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
        </div>
      )}

      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          Didn&apos;t receive the code?
        </p>
        <Button 
          variant="ghost" 
          onClick={handleSendOTP}
          disabled={loading}
          className="text-primary"
          data-testid="resend-otp-btn"
        >
          Resend OTP
        </Button>
      </div>

      <Button 
        variant="outline" 
        className="w-full"
        onClick={() => {
          if (hasPin || hasBiometric) {
            setStep("auth-method");
          } else {
            setStep("phone");
          }
          setOtp("");
        }}
        disabled={loading}
      >
        Back
      </Button>
    </div>
  );

  const renderPINStep = () => (
    <div className="space-y-6">
      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={pin}
          onChange={(value) => {
            setPin(value);
            if (value.length === 6) {
              handlePinLogin(value);
            }
          }}
          disabled={loading}
          type="password"
          data-testid="pin-input"
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={1} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={2} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={3} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={4} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={5} className="w-12 h-14 text-xl" />
          </InputOTPGroup>
        </InputOTP>
      </div>

      {loading && (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
        </div>
      )}

      <div className="text-center">
        <Button 
          variant="ghost" 
          onClick={handleForgotPin}
          disabled={loading}
          className="text-primary text-sm"
          data-testid="forgot-pin-btn"
        >
          Forgot PIN?
        </Button>
      </div>

      <Button 
        variant="outline" 
        className="w-full"
        onClick={() => {
          setStep("auth-method");
          setPin("");
        }}
        disabled={loading}
      >
        Back
      </Button>
    </div>
  );

  const renderForgotPinStep = () => (
    <div className="space-y-6">
      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-center">
        <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
          🔒 PIN Reset Required
        </p>
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
          After verification, you must set a new PIN to login
        </p>
      </div>

      <p className="text-sm text-center text-muted-foreground">
        Enter the OTP sent to <span className="font-semibold">{countryCode} {phone}</span>
      </p>

      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={otp}
          onChange={(value) => {
            setOtp(value);
            if (value.length === 6) {
              handleResetPinOTP(value);
            }
          }}
          disabled={loading}
          data-testid="reset-otp-input"
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={1} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={2} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={3} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={4} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={5} className="w-12 h-14 text-xl" />
          </InputOTPGroup>
        </InputOTP>
      </div>

      {loading && (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
        </div>
      )}

      <Button 
        variant="outline" 
        className="w-full"
        onClick={() => {
          setStep("auth-method");
          setOtp("");
        }}
        disabled={loading}
      >
        Back
      </Button>
    </div>
  );

  const getStepTitle = () => {
    switch (step) {
      case "phone": return "Welcome";
      case "auth-method": return "Login";
      case "otp": return "Enter OTP";
      case "pin": return "Enter PIN";
      case "forgot-pin": return "Reset PIN";
      default: return "Welcome";
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case "phone": return "Sign in with your phone number to continue";
      case "auth-method": return `Logging in as ${countryCode} ${phone}`;
      case "otp": return `We sent a 6-digit code to ${countryCode} ${phone}`;
      case "pin": return "Enter your 6-digit PIN";
      case "forgot-pin": return "Verify OTP to create new PIN";
      default: return "";
    }
  };

  return (
    <div className="min-h-screen bg-background flex relative" data-testid="auth-page">
      {/* Watermark */}
      <div className="fixed inset-0 pointer-events-none z-0 flex items-center justify-center">
        <img 
          src={LOGO_URL} 
          alt="Lunawat Foundation" 
          className="w-64 h-64 md:w-96 md:h-96 object-contain opacity-5"
        />
      </div>
      
      {/* Left Section - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary/5 flex-col justify-center items-center p-12 relative z-10">
        <div className="max-w-md animate-fade-in">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Logo" className="w-16 h-16 rounded-full object-cover" />
            <h1 className="text-4xl font-bold text-foreground">LUNAWAT FOUNDATION</h1>
          </div>
          
          <p className="text-xl text-muted-foreground mb-12">
            Connect with friends, share moments, and stay in touch with the people who matter most.
          </p>

          <div className="space-y-6">
            <div className="flex items-center gap-4 animate-fade-in stagger-1">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Build Your Network</h3>
                <p className="text-sm text-muted-foreground">Connect with friends and discover new people</p>
              </div>
            </div>

            <div className="flex items-center gap-4 animate-fade-in stagger-2">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Real-time Messaging</h3>
                <p className="text-sm text-muted-foreground">Chat instantly with your connections</p>
              </div>
            </div>

            <div className="flex items-center gap-4 animate-fade-in stagger-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Secure & Private</h3>
                <p className="text-sm text-muted-foreground">Your data is always protected</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <Card className="w-full max-w-md border-0 shadow-xl animate-fade-in" data-testid="auth-card">
          <CardHeader className="text-center pb-2">
            {/* Logo and Title - Centered and Big */}
            <div className="flex flex-col items-center justify-center mb-6">
              <img 
                src={LOGO_URL} 
                alt="Logo" 
                className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover shadow-lg mb-4" 
              />
              <h1 className="text-2xl md:text-3xl font-bold text-center">LUNAWAT FOUNDATION</h1>
            </div>
            <CardTitle className="text-2xl font-bold">
              {getStepTitle()}
            </CardTitle>
            <CardDescription>
              {getStepDescription()}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {step === "phone" && renderPhoneStep()}
            {step === "auth-method" && renderAuthMethodStep()}
            {step === "otp" && renderOTPStep()}
            {step === "pin" && renderPINStep()}
            {step === "forgot-pin" && renderForgotPinStep()}
            
            {/* Download App Button - Show on phone step only */}
            {step === "phone" && !isInstalled && (
              <div className="mt-6 pt-6 border-t">
                <Button 
                  variant="outline" 
                  className="w-full h-12 gap-2"
                  onClick={handleInstallClick}
                  data-testid="download-app-btn"
                >
                  <Download className="w-5 h-5" />
                  Download App
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  Install for faster access & offline support
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* iOS Install Instructions Modal */}
      {showIOSModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-sm animate-fade-in">
            <CardContent className="p-6">
              <div className="text-center mb-4">
                <Smartphone className="w-12 h-12 mx-auto text-primary mb-3" />
                <h3 className="font-semibold text-lg">Install on iPhone/iPad</h3>
              </div>
              
              <div className="space-y-4 text-sm">
                <div className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">1</span>
                  <p>Tap the <strong>Share</strong> button at the bottom of Safari</p>
                </div>
                <div className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">2</span>
                  <p>Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></p>
                </div>
                <div className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">3</span>
                  <p>Tap <strong>&quot;Add&quot;</strong> in the top right corner</p>
                </div>
              </div>

              <Button className="w-full mt-6" onClick={() => setShowIOSModal(false)}>
                Got it!
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AuthPage;
